import React, { useState } from 'react';
import config from './config';

const CompetitiveIntelligencePage = ({ user, onBack, setPage, onUsageUpdate }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState({ percent: 0, message: '' });

  // Form data
  const [formData, setFormData] = useState({
    competitors: '',
    technologyArea: '',
    yourCompany: '',
    timeframe: '5'
  });

  // Results
  const [results, setResults] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [suggestingField, setSuggestingField] = useState(false);
  const [suggestedFields, setSuggestedFields] = useState([]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Suggest technology areas using AI
  const suggestFields = async () => {
    if (!formData.competitors) {
      setError('Please enter competitor names first');
      return;
    }

    setSuggestingField(true);
    setSuggestedFields([]);

    try {
      const response = await fetch(`${config.API_URL}/api/suggest-field`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: formData.competitors,
          description: 'Competitor analysis for: ' + formData.competitors
        })
      });

      const data = await response.json();
      if (data.success && data.fields) {
        setSuggestedFields(data.fields);
      }
    } catch (err) {
      console.error('Error suggesting fields:', err);
    } finally {
      setSuggestingField(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.competitors.trim() && !formData.technologyArea.trim()) {
      setError('Please enter at least one competitor or a technology area');
      return;
    }

    setLoading(true);
    setError('');
    setStep(2);
    setProgress({ percent: 10, message: 'Starting competitive analysis...' });

    try {
      const token = localStorage.getItem('token');

      const progressUpdates = [
        { percent: 15, message: 'Identifying competitor patents...' },
        { percent: 30, message: 'Analyzing patent portfolios...' },
        { percent: 45, message: 'Mapping technology focus areas...' },
        { percent: 60, message: 'Calculating filing trends...' },
        { percent: 75, message: 'Identifying strategic patterns...' },
        { percent: 90, message: 'Generating intelligence report...' }
      ];

      let progressIndex = 0;
      const progressInterval = setInterval(() => {
        if (progressIndex < progressUpdates.length) {
          setProgress(progressUpdates[progressIndex]);
          progressIndex++;
        }
      }, 1800);

      const response = await fetch(`${config.API_URL}/api/competitive-intelligence`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          competitors: formData.competitors,
          technology_area: formData.technologyArea,
          your_company: formData.yourCompany,
          timeframe_years: parseInt(formData.timeframe),
          token: token
        })
      });

      clearInterval(progressInterval);

      const data = await response.json();

      if (data.success) {
        setResults(data);
        setStep(3);
        if (onUsageUpdate) onUsageUpdate();
      } else {
        setError(data.error || 'Error performing competitive analysis');
        setStep(1);
      }
    } catch (err) {
      setError('Error connecting to server. Please try again.');
      setStep(1);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetJourney = () => {
    setStep(1);
    setFormData({ competitors: '', technologyArea: '', yourCompany: '', timeframe: '5' });
    setResults(null);
    setError('');
  };

  const getThreatColor = (level) => {
    switch (level) {
      case 'HIGH': return { bg: '#fef2f2', border: '#dc2626', text: '#dc2626' };
      case 'MEDIUM': return { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' };
      case 'LOW': return { bg: '#dcfce7', border: '#16a34a', text: '#166534' };
      default: return { bg: '#f1f5f9', border: '#64748b', text: '#475569' };
    }
  };

  // Save report to database
  const saveReport = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please log in to save reports');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${config.API_URL}/api/saved-individual-reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          report_type: 'competitive-intelligence',
          title: formData?.competitors || formData?.technologyArea || 'Competitive Intelligence Report',
          input_data: formData,
          results_data: results,
          token: token
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        alert(data.error || 'Failed to save report');
      }
    } catch (err) {
      alert('Error saving report');
    } finally {
      setSaving(false);
    }
  };

  // Download as HTML
  const downloadHTML = () => {
    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <title>Competitive Intelligence Report</title>
  <style>
    body { font-family: Arial, sans-serif; max-width: 900px; margin: 0 auto; padding: 20px; line-height: 1.6; }
    h1 { color: #1e293b; border-bottom: 3px solid #7c3aed; padding-bottom: 10px; }
    h2 { color: #7c3aed; margin-top: 30px; }
    .summary-box { background: #f8fafc; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #7c3aed; }
    .stats { display: flex; gap: 20px; margin: 20px 0; }
    .stat { background: #f3e8ff; color: #7c3aed; padding: 15px 25px; border-radius: 8px; text-align: center; }
    .stat-number { font-size: 24px; font-weight: bold; }
    .stat-label { font-size: 12px; }
    .competitor { background: white; border: 1px solid #e2e8f0; padding: 20px; border-radius: 8px; margin: 15px 0; }
    .competitor-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
    .competitor-name { font-size: 18px; font-weight: bold; color: #1e293b; }
    .threat-high { background: #fee2e2; color: #dc2626; padding: 4px 12px; border-radius: 12px; font-size: 12px; }
    .threat-medium { background: #fef3c7; color: #d97706; padding: 4px 12px; border-radius: 12px; font-size: 12px; }
    .threat-low { background: #dcfce7; color: #16a34a; padding: 4px 12px; border-radius: 12px; font-size: 12px; }
    .focus-areas { margin: 10px 0; }
    .focus-tag { display: inline-block; background: #e0e7ff; color: #4338ca; padding: 4px 10px; border-radius: 4px; margin: 3px; font-size: 12px; }
    .insights-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin: 20px 0; }
    .insight-box { background: #f8fafc; padding: 15px; border-radius: 8px; }
    .insight-box h3 { margin-top: 0; color: #1e293b; font-size: 14px; }
    .insight-box ul { margin: 0; padding-left: 20px; }
    .insight-box li { margin: 8px 0; }
    .trend { display: flex; align-items: center; gap: 10px; padding: 10px; background: #f8fafc; border-radius: 6px; margin: 8px 0; }
    .trend-up { color: #16a34a; }
    .trend-down { color: #dc2626; }
    .trend-stable { color: #6b7280; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; color: #64748b; font-size: 12px; }
  </style>
</head>
<body>
  <h1>Competitive Intelligence Report</h1>
  
  <div class="stats">
    <div class="stat">
      <div class="stat-number">${results?.competitors?.length || 0}</div>
      <div class="stat-label">Competitors</div>
    </div>
    <div class="stat">
      <div class="stat-number">${results?.total_patents || 0}</div>
      <div class="stat-label">Patents Analyzed</div>
    </div>
    <div class="stat">
      <div class="stat-number">${results?.timeframe || 5} yrs</div>
      <div class="stat-label">Timeframe</div>
    </div>
  </div>

  <h2>Executive Summary</h2>
  <div class="summary-box">
    <p><strong>${results?.summary?.headline || ''}</strong></p>
    <p>${results?.summary?.executive_summary || ''}</p>
  </div>

  <h2>Competitor Profiles</h2>
  ${(results?.competitors || []).map(c => `
    <div class="competitor">
      <div class="competitor-header">
        <span class="competitor-name">${c.name || 'Unknown'}</span>
        <span class="threat-${(c.threat_level || 'medium').toLowerCase()}">${c.threat_level || 'MEDIUM'} Threat</span>
      </div>
      <p><strong>Patents:</strong> ${c.patent_count || 0}</p>
      <p>${c.analysis || ''}</p>
      <div class="focus-areas">
        <strong>Focus Areas:</strong>
        ${(c.focus_areas || []).map(area => `<span class="focus-tag">${area}</span>`).join('')}
      </div>
      ${c.recent_filings && c.recent_filings.length > 0 ? `
        <p><strong>Recent Notable Filings:</strong></p>
        <ul>
          ${c.recent_filings.slice(0, 3).map(f => `<li>${f.patent_number}: ${f.title}</li>`).join('')}
        </ul>
      ` : ''}
    </div>
  `).join('')}

  <h2>Strategic Insights</h2>
  <div class="insights-grid">
    <div class="insight-box">
      <h3>Opportunities</h3>
      <ul>
        ${(results?.strategic_insights?.opportunities || []).map(item => `<li>${item}</li>`).join('') || '<li>No opportunities identified</li>'}
      </ul>
    </div>
    <div class="insight-box">
      <h3>Threats</h3>
      <ul>
        ${(results?.strategic_insights?.threats || []).map(item => `<li>${item}</li>`).join('') || '<li>No threats identified</li>'}
      </ul>
    </div>
  </div>

  <h2>Recommendations</h2>
  <div class="insight-box">
    <ul>
      ${(results?.strategic_insights?.recommendations || []).map(item => `<li>${item}</li>`).join('') || '<li>No recommendations available</li>'}
    </ul>
  </div>

  ${results?.technology_trends && results.technology_trends.length > 0 ? `
    <h2>Technology Trends</h2>
    ${results.technology_trends.map(t => `
      <div class="trend">
        <strong>${t.area || 'Unknown'}</strong>
        <span class="trend-${t.trend === 'UP' ? 'up' : t.trend === 'DOWN' ? 'down' : 'stable'}">
          ${t.trend === 'UP' ? '↑ Growing' : t.trend === 'DOWN' ? '↓ Declining' : '→ Stable'}
        </span>
        <span>${t.patents || 0} patents</span>
      </div>
    `).join('')}
  ` : ''}

  <div class="footer">
    <p>Generated by PatTrak - ${new Date().toLocaleDateString()}</p>
    <p>Technology Area: ${formData?.technologyArea || 'Not specified'} | Competitors: ${formData?.competitors || 'Not specified'}</p>
  </div>
</body>
</html>`;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `PatTrak_Competitive_${(formData?.competitors || formData?.technologyArea || 'report').substring(0, 20).replace(/[^a-z0-9]/gi, '_')}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Download as PDF
  const downloadPDF = async () => {
    if (!results) return;
    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <title>Competitive Intelligence: ${formData?.competitors || formData?.technologyArea || 'Report'}</title>
  <style>
    body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 40px; line-height: 1.6; }
    h1 { color: #1e293b; font-size: 24px; }
    h2 { color: #7c3aed; font-size: 18px; border-bottom: 2px solid #7c3aed; padding-bottom: 8px; margin-top: 30px; }
    .section { background: #f8fafc; padding: 20px; border-radius: 8px; margin: 15px 0; }
    .stats { display: flex; gap: 20px; margin: 15px 0; }
    .stat { background: #f3e8ff; padding: 15px; border-radius: 8px; text-align: center; flex: 1; }
    .stat-num { font-size: 24px; font-weight: bold; color: #7c3aed; }
    .stat-label { font-size: 12px; color: #64748b; }
    ul { margin: 10px 0; padding-left: 20px; }
    li { margin: 8px 0; }
    .competitor { background: white; border: 1px solid #e2e8f0; padding: 15px; margin: 10px 0; border-radius: 8px; }
    .threat-high { color: #dc2626; font-weight: bold; }
    .threat-medium { color: #d97706; font-weight: bold; }
    .threat-low { color: #16a34a; font-weight: bold; }
    .focus-tag { display: inline-block; background: #e0e7ff; color: #4338ca; padding: 3px 8px; border-radius: 4px; margin: 2px; font-size: 11px; }
    .trend { background: #f8fafc; padding: 10px; margin: 5px 0; border-radius: 6px; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; color: #64748b; font-size: 12px; }
  </style>
</head>
<body>
  <h1>Competitive Intelligence Report</h1>
  <p><strong>Competitors:</strong> ${formData?.competitors || 'Not specified'}</p>
  <p><strong>Technology Area:</strong> ${formData?.technologyArea || 'Not specified'}</p>
  <p><strong>Generated:</strong> ${new Date().toLocaleDateString()}</p>

  <div class="stats">
    <div class="stat"><div class="stat-num">${results?.competitors?.length || 0}</div><div class="stat-label">Competitors</div></div>
    <div class="stat"><div class="stat-num">${results?.total_patents || 0}</div><div class="stat-label">Patents</div></div>
    <div class="stat"><div class="stat-num">${results?.timeframe || 5} yrs</div><div class="stat-label">Timeframe</div></div>
  </div>

  <h2>Executive Summary</h2>
  <div class="section">
    <p><strong>${results?.summary?.headline || ''}</strong></p>
    <p>${results?.summary?.executive_summary || ''}</p>
  </div>

  ${results?.competitors && results.competitors.length > 0 ? '<h2>Competitor Profiles</h2>' + results.competitors.map(c => '<div class="competitor"><strong>' + c.name + '</strong> - ' + c.patent_count + ' patents <span class="threat-' + (c.threat_level || 'medium').toLowerCase() + '">(' + c.threat_level + ' Threat)</span><p>' + (c.analysis || '') + '</p><div>' + (c.focus_areas || []).map(a => '<span class="focus-tag">' + a + '</span>').join('') + '</div></div>').join('') : ''}

  <h2>Strategic Insights</h2>
  <div class="section">
    <h3>Opportunities</h3>
    <ul>${(results?.strategic_insights?.opportunities || []).map(item => '<li>' + item + '</li>').join('') || '<li>None identified</li>'}</ul>
    <h3>Threats</h3>
    <ul>${(results?.strategic_insights?.threats || []).map(item => '<li>' + item + '</li>').join('') || '<li>None identified</li>'}</ul>
  </div>

  <h2>Recommendations</h2>
  <div class="section"><ul>${(results?.strategic_insights?.recommendations || []).map(item => '<li>' + item + '</li>').join('') || '<li>No recommendations</li>'}</ul></div>

  ${results?.technology_trends && results.technology_trends.length > 0 ? '<h2>Technology Trends</h2><div class="section">' + results.technology_trends.map(t => '<div class="trend"><strong>' + t.area + '</strong> - ' + (t.trend === 'UP' ? '↑ Growing' : t.trend === 'DOWN' ? '↓ Declining' : '→ Stable') + ' (' + t.patents + ' patents)</div>').join('') + '</div>' : ''}

  <div class="footer">Generated by PatTrak - Patent Intelligence Made Simple</div>
</body>
</html>`;
    try {
      const response = await fetch(`${config.API_URL}/api/generate-invention-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          report_html: htmlContent,
          title: formData?.competitors || formData?.technologyArea || 'Competitive_Intel'
        })
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `PatTrak_Competitive_${(formData?.competitors || formData?.technologyArea || 'report').substring(0, 20).replace(/[^a-z0-9]/gi, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        alert('Failed to generate PDF');
      }
    } catch (error) {
      console.error('PDF error:', error);
      alert('Failed to generate PDF');
    }
  };

  // Tier gate - only Premium and Pro users can access
  if (!user || user.tier === 'free') {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
        <header style={{ padding: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0369a1', fontSize: '14px', fontWeight: '500' }}>
            ← Back
          </button>
        </header>
        
        <main style={{ maxWidth: '600px', margin: '80px auto', padding: '0 16px', textAlign: 'center' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '48px 32px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
            <div style={{ fontSize: '64px', marginBottom: '24px' }}>🔒</div>
            <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', marginBottom: '12px' }}>
              Competitive Intelligence
            </h1>
            <p style={{ color: '#64748b', fontSize: '16px', lineHeight: '1.6', marginBottom: '32px' }}>
              Analyze competitor patent portfolios and strategic positioning.
            </p>
            
            <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '12px', padding: '20px', marginBottom: '32px' }}>
              <p style={{ color: '#0369a1', fontSize: '14px', margin: 0 }}>
                {!user 
                  ? 'Please register or log in to access this tool.'
                  : 'This tool requires a Premium or Pro subscription.'
                }
              </p>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '280px', margin: '0 auto' }}>
              {!user ? (
                <>
                  <button onClick={() => setPage('register')} style={{ padding: '14px 28px', background: '#0369a1', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '16px' }}>
                    Register Free
                  </button>
                  <button onClick={() => setPage('login')} style={{ padding: '14px 28px', background: 'white', color: '#0369a1', border: '1px solid #0369a1', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '16px' }}>
                    Log In
                  </button>
                </>
              ) : (
                <button onClick={() => setPage('pricing')} style={{ padding: '14px 28px', background: '#0369a1', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '16px' }}>
                  Upgrade to Premium
                </button>
              )}
              <button onClick={onBack} style={{ padding: '10px', background: 'none', color: '#64748b', border: 'none', cursor: 'pointer', fontSize: '14px' }}>
                Maybe Later
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <button
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            color: '#0369a1',
            cursor: 'pointer',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: 0,
            marginBottom: '16px'
          }}
        >
          ← Back to Search
        </button>

        <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', margin: 0 }}>
          Competitive Intelligence
        </h1>
        <p style={{ color: '#64748b', marginTop: '8px', fontSize: '16px' }}>
          Analyze competitor patent portfolios and strategic positioning
        </p>
      </div>

      {/* Progress Steps */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '32px',
        padding: '16px',
        background: '#f8fafc',
        borderRadius: '8px'
      }}>
        {[
          { num: 1, label: 'Define Competitors' },
          { num: 2, label: 'Analyzing' },
          { num: 3, label: 'Intelligence Report' }
        ].map((s, idx) => (
          <React.Fragment key={s.num}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: step >= s.num ? '#0369a1' : '#e2e8f0',
                color: step >= s.num ? 'white' : '#64748b',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '600',
                fontSize: '14px'
              }}>
                {step > s.num ? '✓' : s.num}
              </div>
              <span style={{
                color: step >= s.num ? '#0369a1' : '#64748b',
                fontWeight: step === s.num ? '600' : '400',
                fontSize: '14px'
              }}>
                {s.label}
              </span>
            </div>
            {idx < 2 && (
              <div style={{
                flex: 1,
                height: '2px',
                background: step > s.num ? '#0369a1' : '#e2e8f0',
                margin: '0 16px'
              }} />
            )}
          </React.Fragment>
        ))}
      </div>

      {error && (
        <div style={{
          padding: '12px 16px',
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#dc2626',
          marginBottom: '24px'
        }}>
          {error}
        </div>
      )}

      {/* Step 1: Input Form */}
      {step === 1 && (
        <div style={{
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '24px'
        }}>
          <div style={{
            background: '#faf5ff',
            border: '1px solid #e9d5ff',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <h3 style={{ margin: '0 0 8px 0', color: '#7c3aed', fontSize: '14px', fontWeight: '600' }}>
              What is Competitive Intelligence?
            </h3>
            <p style={{ margin: 0, color: '#6b21a8', fontSize: '14px', lineHeight: '1.5' }}>
              Patent competitive intelligence reveals what your competitors are developing, where they're 
              investing R&D resources, and potential threats or opportunities in your market. This helps 
              you make strategic decisions about your own innovation roadmap.
            </p>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>
              Competitors to Analyze
            </label>
            <textarea
              value={formData.competitors}
              onChange={(e) => handleInputChange('competitors', e.target.value)}
              placeholder="Enter competitor names (one per line):&#10;Apple Inc&#10;Samsung Electronics&#10;Google LLC"
              rows={4}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '15px',
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
            />
            <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#6b7280' }}>
              Enter company names as they appear on patents (e.g., "Apple Inc", "International Business Machines Corporation")
            </p>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>
              Technology Area <span style={{ color: '#94a3b8', fontWeight: 'normal' }}>(Optional - helps focus the analysis)</span>
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                value={formData.technologyArea}
                onChange={(e) => handleInputChange('technologyArea', e.target.value)}
                placeholder="e.g., Machine learning, Battery technology, Wireless communications"
                style={{
                  flex: 1,
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '15px',
                  boxSizing: 'border-box'
                }}
              />
              <button
                type="button"
                onClick={suggestFields}
                disabled={suggestingField || !formData.competitors}
                style={{
                  padding: '10px 16px',
                  borderRadius: '8px',
                  border: '1px solid #0369a1',
                  background: suggestingField ? '#e0f2fe' : 'white',
                  color: '#0369a1',
                  cursor: suggestingField || !formData.competitors ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  whiteSpace: 'nowrap'
                }}
              >
                {suggestingField ? 'Thinking...' : 'Suggest'}
              </button>
            </div>
            {suggestedFields.length > 0 && (
              <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {suggestedFields.map((field, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => { handleInputChange('technologyArea', field); setSuggestedFields([]); }}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '16px',
                      border: '1px solid #0369a1',
                      background: '#f0f9ff',
                      color: '#0369a1',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    {field}
                  </button>
                ))}
              </div>
            )}
            <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#6b7280' }}>
              Focus the analysis on a specific technology area
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>
                Your Company (Optional)
              </label>
              <input
                type="text"
                value={formData.yourCompany}
                onChange={(e) => handleInputChange('yourCompany', e.target.value)}
                placeholder="e.g., Acme Corporation"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '15px',
                  boxSizing: 'border-box'
                }}
              />
              <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#6b7280' }}>
                Include for comparison analysis
              </p>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>
                Timeframe
              </label>
              <select
                value={formData.timeframe}
                onChange={(e) => handleInputChange('timeframe', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '15px',
                  boxSizing: 'border-box',
                  background: 'white'
                }}
              >
                <option value="3">Last 3 years</option>
                <option value="5">Last 5 years</option>
                <option value="10">Last 10 years</option>
                <option value="0">All time</option>
              </select>
            </div>
          </div>

          <button
            onClick={handleSubmit}
            style={{
              width: '100%',
              padding: '14px',
              background: '#7c3aed',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Analyze Competitors →
          </button>
        </div>
      )}

      {/* Step 2: Loading */}
      {step === 2 && (
        <div style={{
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '48px',
          textAlign: 'center'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            border: '4px solid #e2e8f0',
            borderTopColor: '#7c3aed',
            borderRadius: '50%',
            margin: '0 auto 24px',
            animation: 'spin 1s linear infinite'
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          
          <h2 style={{ margin: '0 0 8px 0', color: '#1e293b' }}>Analyzing Competitors</h2>
          <p style={{ color: '#64748b', marginBottom: '24px' }}>{progress.message}</p>
          
          <div style={{
            background: '#f1f5f9',
            borderRadius: '8px',
            height: '8px',
            overflow: 'hidden',
            maxWidth: '400px',
            margin: '0 auto'
          }}>
            <div style={{
              background: '#7c3aed',
              height: '100%',
              width: `${progress.percent}%`,
              transition: 'width 0.5s ease'
            }} />
          </div>
          <p style={{ color: '#94a3b8', fontSize: '14px', marginTop: '12px' }}>{progress.percent}% complete</p>

          <div style={{
            background: '#f8fafc',
            borderRadius: '8px',
            padding: '16px',
            marginTop: '24px',
            textAlign: 'left',
            maxWidth: '500px',
            margin: '24px auto 0'
          }}>
            <p style={{ margin: 0, fontSize: '14px', color: '#64748b' }}>
              <strong>What we're analyzing:</strong> Patent filing volumes, technology focus areas, 
              recent activity trends, key inventors, and strategic positioning across competitors.
            </p>
          </div>
        </div>
      )}

      {/* Step 3: Results */}
      {step === 3 && results && (
        <div>
          {/* Summary Banner */}
          <div style={{
            background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            color: 'white'
          }}>
            <h2 style={{ margin: '0 0 8px 0', fontSize: '24px' }}>
              Competitive Intelligence Report
            </h2>
            <p style={{ margin: 0, opacity: 0.9 }}>
              {results.summary?.headline}
            </p>
            <div style={{ display: 'flex', gap: '24px', marginTop: '16px' }}>
              <div>
                <div style={{ fontSize: '28px', fontWeight: '700' }}>{results.competitors_analyzed || 0}</div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>Competitors</div>
              </div>
              <div>
                <div style={{ fontSize: '28px', fontWeight: '700' }}>{results.total_patents || 0}</div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>Patents Analyzed</div>
              </div>
              <div>
                <div style={{ fontSize: '28px', fontWeight: '700' }}>{results.timeframe || '5'} yrs</div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>Timeframe</div>
              </div>
            </div>
          </div>

          {/* Executive Summary */}
          <div style={{
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#1e293b' }}>Executive Summary</h3>
            <p style={{ margin: 0, color: '#475569', lineHeight: '1.6' }}>
              {results.summary?.executive_summary}
            </p>
          </div>

          {/* Competitor Profiles */}
          {results.competitors && results.competitors.length > 0 && (
            <div style={{
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#1e293b' }}>Competitor Profiles</h3>
              
              {results.competitors.map((competitor, idx) => {
                const threatColors = getThreatColor(competitor.threat_level);
                return (
                  <div key={idx} style={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '12px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div>
                        <h4 style={{ margin: '0 0 4px 0', color: '#1e293b', fontSize: '16px' }}>
                          {competitor.name}
                        </h4>
                        <span style={{ color: '#64748b', fontSize: '14px' }}>
                          {competitor.patent_count} patents in portfolio
                        </span>
                      </div>
                      <span style={{
                        background: threatColors.bg,
                        color: threatColors.text,
                        border: `1px solid ${threatColors.border}`,
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {competitor.threat_level} Threat
                      </span>
                    </div>
                    
                    <div style={{ marginBottom: '12px' }}>
                      <strong style={{ color: '#374151', fontSize: '13px' }}>Technology Focus:</strong>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                        {competitor.focus_areas?.map((area, aIdx) => (
                          <span key={aIdx} style={{
                            background: '#f1f5f9',
                            color: '#475569',
                            padding: '4px 10px',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            {area}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <p style={{ margin: '0', color: '#475569', fontSize: '14px', lineHeight: '1.5' }}>
                      {competitor.analysis}
                    </p>
                    
                    {competitor.recent_filings && competitor.recent_filings.length > 0 && (
                      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
                        <strong style={{ color: '#374151', fontSize: '13px' }}>Recent Notable Filings:</strong>
                        <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                          {competitor.recent_filings.slice(0, 3).map((filing, fIdx) => (
                            <li key={fIdx} style={{ color: '#64748b', fontSize: '13px', marginBottom: '4px' }}>
                              <a 
                                href={`https://patents.google.com/patent/US${filing.patent_number}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#0369a1' }}
                              >
                                US{filing.patent_number}
                              </a>: {filing.title}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Strategic Insights */}
          {results.strategic_insights && (
            <div style={{
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#1e293b' }}>Strategic Insights</h3>
              
              {results.strategic_insights.opportunities && (
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ margin: '0 0 8px 0', color: '#16a34a', fontSize: '14px' }}>Opportunities</h4>
                  <ul style={{ margin: 0, paddingLeft: '20px', color: '#475569' }}>
                    {results.strategic_insights.opportunities.map((opp, idx) => (
                      <li key={idx} style={{ marginBottom: '6px', fontSize: '14px' }}>{opp}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {results.strategic_insights.threats && (
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ margin: '0 0 8px 0', color: '#dc2626', fontSize: '14px' }}>Threats</h4>
                  <ul style={{ margin: 0, paddingLeft: '20px', color: '#475569' }}>
                    {results.strategic_insights.threats.map((threat, idx) => (
                      <li key={idx} style={{ marginBottom: '6px', fontSize: '14px' }}>{threat}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {results.strategic_insights.recommendations && (
                <div>
                  <h4 style={{ margin: '0 0 8px 0', color: '#0369a1', fontSize: '14px' }}>Recommendations</h4>
                  <ul style={{ margin: 0, paddingLeft: '20px', color: '#475569' }}>
                    {results.strategic_insights.recommendations.map((rec, idx) => (
                      <li key={idx} style={{ marginBottom: '6px', fontSize: '14px' }}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Technology Trends */}
          {results.technology_trends && (
            <div style={{
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#1e293b' }}>Technology Trends</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                {results.technology_trends.map((trend, idx) => (
                  <div key={idx} style={{
                    background: '#f8fafc',
                    borderRadius: '8px',
                    padding: '12px'
                  }}>
                    <div style={{ fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>{trend.area}</div>
                    <div style={{ fontSize: '13px', color: '#64748b' }}>
                      {trend.trend === 'UP' ? '↑ Growing' : trend.trend === 'DOWN' ? '↓ Declining' : '→ Stable'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>{trend.patents} patents</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* What You Learned */}
          <div style={{
            background: '#faf5ff',
            border: '1px solid #e9d5ff',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ margin: '0 0 12px 0', color: '#7c3aed' }}>What You Learned</h3>
            <ul style={{ margin: 0, paddingLeft: '20px', color: '#6b21a8' }}>
              <li style={{ marginBottom: '6px' }}>How to analyze competitor patent portfolios</li>
              <li style={{ marginBottom: '6px' }}>Which technology areas competitors are investing in</li>
              <li style={{ marginBottom: '6px' }}>How to identify strategic threats and opportunities</li>
              <li>How to use patent data for competitive strategy</li>
            </ul>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button
              onClick={resetJourney}
              style={{
                padding: '12px 24px',
                background: 'white',
                color: '#0369a1',
                border: '1px solid #0369a1',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              ← Analyze Different Competitors
            </button>
            <button
              onClick={saveReport}
              disabled={saving}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '500',
                color: 'white',
                background: saveSuccess ? '#16a34a' : '#0369a1',
                border: 'none',
                borderRadius: '8px',
                cursor: saving ? 'not-allowed' : 'pointer'
              }}
            >
              {saving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Report'}
            </button>
            <button
              onClick={downloadHTML}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                background: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Download HTML
            </button>
            <button
              onClick={downloadPDF}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '500',
                color: 'white',
                background: '#0369a1',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Download PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompetitiveIntelligencePage;
